/**
 * Czysta logika kolizji AABB vs agent (koło) — bez Phaser.
 * Używana przez WalkController i testy jednostkowe.
 *
 * WAVE3 G1: depenetracja — gdy start jest WEWNĄTRZ collidera, dopuszczamy
 * kroki zmniejszające głębokość penetracji (inaczej agent jest sparaliżowany).
 */
import type { ColliderRect, WorldMoveBounds } from './types.js';

/** Czy koło o środku (cx,cy) i promieniu r przecina prostokąt. */
export function circleHitsRect(
  cx: number,
  cy: number,
  r: number,
  rect: ColliderRect,
): boolean {
  const nearestX = Math.max(rect.x, Math.min(cx, rect.x + rect.width));
  const nearestY = Math.max(rect.y, Math.min(cy, rect.y + rect.height));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  return dx * dx + dy * dy < r * r;
}

export function collidesAt(
  x: number,
  y: number,
  colliders: readonly ColliderRect[],
  radius: number,
): boolean {
  for (const c of colliders) {
    if (circleHitsRect(x, y, radius, c)) return true;
  }
  return false;
}

/**
 * Głębokość penetracji koła w jeden prostokąt (0 = brak kolizji).
 * Im większa, tym głębiej agent jest w bloku.
 */
export function circleRectPenetration(
  cx: number,
  cy: number,
  r: number,
  rect: ColliderRect,
): number {
  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;

  if (cx >= left && cx <= right && cy >= top && cy <= bottom) {
    const dl = cx - left;
    const dr = right - cx;
    const dt = cy - top;
    const db = bottom - cy;
    const minEdge = Math.min(dl, dr, dt, db);
    return r + minEdge;
  }

  const nearestX = Math.max(left, Math.min(cx, right));
  const nearestY = Math.max(top, Math.min(cy, bottom));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  const dist = Math.hypot(dx, dy);
  if (dist >= r) return 0;
  return r - dist;
}

/** Max penetracja względem wszystkich colliderów. */
export function maxPenetrationDepth(
  x: number,
  y: number,
  colliders: readonly ColliderRect[],
  radius: number,
): number {
  let m = 0;
  for (const c of colliders) {
    m = Math.max(m, circleRectPenetration(x, y, radius, c));
  }
  return m;
}

/**
 * Wektor wypchnięcia koła z prostokąta (MTV + mały margines).
 * Zerowy gdy brak kolizji.
 */
export function circleRectPush(
  cx: number,
  cy: number,
  r: number,
  rect: ColliderRect,
  margin = 1.5,
): { x: number; y: number } {
  const pen = circleRectPenetration(cx, cy, r, rect);
  if (pen <= 0) return { x: 0, y: 0 };

  const left = rect.x;
  const right = rect.x + rect.width;
  const top = rect.y;
  const bottom = rect.y + rect.height;

  if (cx >= left && cx <= right && cy >= top && cy <= bottom) {
    const dl = cx - left;
    const dr = right - cx;
    const dt = cy - top;
    const db = bottom - cy;
    const minEdge = Math.min(dl, dr, dt, db);
    const dist = minEdge + r + margin;
    if (minEdge === dl) return { x: -dist, y: 0 };
    if (minEdge === dr) return { x: dist, y: 0 };
    if (minEdge === dt) return { x: 0, y: -dist };
    return { x: 0, y: dist };
  }

  const nearestX = Math.max(left, Math.min(cx, right));
  const nearestY = Math.max(top, Math.min(cy, bottom));
  let dx = cx - nearestX;
  let dy = cy - nearestY;
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-8) {
    // na krawędzi — wypchnij w dół (bezpieczny default Town)
    return { x: 0, y: pen + margin };
  }
  const need = r - dist + margin;
  dx /= dist;
  dy /= dist;
  return { x: dx * need, y: dy * need };
}

/**
 * Wypchnij pozycję poza wszystkie collidery (konstruktor WalkController / spawn).
 */
export function nudgeOutOfColliders(
  x: number,
  y: number,
  colliders: readonly ColliderRect[],
  radius: number,
  bounds?: WorldMoveBounds,
  maxIter = 48,
): { x: number; y: number } {
  let px = x;
  let py = y;
  for (let iter = 0; iter < maxIter; iter++) {
    if (!collidesAt(px, py, colliders, radius)) {
      return bounds ? clampToMoveBounds(px, py, bounds) : { x: px, y: py };
    }
    let bestPen = 0;
    let pushX = 0;
    let pushY = 0;
    for (const rect of colliders) {
      const pen = circleRectPenetration(px, py, radius, rect);
      if (pen > bestPen) {
        bestPen = pen;
        const p = circleRectPush(px, py, radius, rect);
        pushX = p.x;
        pushY = p.y;
      }
    }
    if (bestPen <= 0 || (Math.abs(pushX) < 1e-8 && Math.abs(pushY) < 1e-8)) {
      break;
    }
    px += pushX;
    py += pushY;
    if (bounds) {
      const c = clampToMoveBounds(px, py, bounds);
      px = c.x;
      py = c.y;
    }
  }
  return bounds ? clampToMoveBounds(px, py, bounds) : { x: px, y: py };
}

export function clampToMoveBounds(x: number, y: number, b: WorldMoveBounds): { x: number; y: number } {
  return {
    x: Math.min(b.maxX, Math.max(b.minX, x)),
    y: Math.min(b.maxY, Math.max(b.minY, y)),
  };
}

function acceptCandidate(
  fromX: number,
  fromY: number,
  candX: number,
  candY: number,
  colliders: readonly ColliderRect[],
  radius: number,
  fromCollides: boolean,
  fromPen: number,
): boolean {
  if (!collidesAt(candX, candY, colliders, radius)) return true;
  if (!fromCollides) return false;
  // Depenetracja: wolno wejść w pozycję z MNIEJSZĄ penetracją
  const pen = maxPenetrationDepth(candX, candY, colliders, radius);
  return pen < fromPen - 1e-4;
}

/**
 * Rozwiązuje ruch z kolizjami (slide po osiach).
 * Cel za blokadą: agent dochodzi do ściany i się zatrzymuje — nie przechodzi przez prostokąt.
 * Start wewnątrz: kroki zmniejszające penetrację są dozwolone (aż do wyjścia).
 */
export function resolveMovement(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  colliders: readonly ColliderRect[],
  radius: number,
  bounds?: WorldMoveBounds,
): { x: number; y: number } {
  let x = toX;
  let y = toY;
  if (bounds) {
    const c = clampToMoveBounds(x, y, bounds);
    x = c.x;
    y = c.y;
  }

  const fromCollides = collidesAt(fromX, fromY, colliders, radius);
  const fromPen = fromCollides
    ? maxPenetrationDepth(fromX, fromY, colliders, radius)
    : 0;

  if (!collidesAt(x, y, colliders, radius)) {
    return { x, y };
  }

  // Gdy start koliduje i cel też — wybierz kandydat z najmniejszą penetracją
  // (w tym sam cel, slide X/Y, oraz lekki push z from).
  if (fromCollides) {
    const tryX = bounds ? clampToMoveBounds(x, fromY, bounds) : { x, y: fromY };
    const tryY = bounds ? clampToMoveBounds(fromX, y, bounds) : { x: fromX, y };
    const push = nudgeOutOfColliders(fromX, fromY, colliders, radius, bounds);
    // Jeden krok w stronę wypchnięcia (nie skacz od razu daleko w stepToward)
    const dx = push.x - fromX;
    const dy = push.y - fromY;
    const dlen = Math.hypot(dx, dy);
    const stepTowardFree =
      dlen > 1e-6
        ? {
            x: fromX + (dx / dlen) * Math.min(dlen, Math.hypot(x - fromX, y - fromY) || 8),
            y: fromY + (dy / dlen) * Math.min(dlen, Math.hypot(x - fromX, y - fromY) || 8),
          }
        : { x: fromX, y: fromY };
    if (bounds) {
      const c = clampToMoveBounds(stepTowardFree.x, stepTowardFree.y, bounds);
      stepTowardFree.x = c.x;
      stepTowardFree.y = c.y;
    }

    const candidates = [
      { x, y },
      tryX,
      tryY,
      stepTowardFree,
      push,
    ];
    let best = { x: fromX, y: fromY };
    let bestPen = fromPen;
    for (const cand of candidates) {
      const pen = maxPenetrationDepth(cand.x, cand.y, colliders, radius);
      if (pen < bestPen - 1e-4) {
        best = { x: cand.x, y: cand.y };
        bestPen = pen;
      }
      if (pen === 0) {
        return { x: cand.x, y: cand.y };
      }
    }
    return best;
  }

  // Normalny przypadek: start wolny — slide tylko po wolnych polach
  let nx = fromX;
  let ny = fromY;

  const tryX = bounds ? clampToMoveBounds(x, fromY, bounds) : { x, y: fromY };
  if (acceptCandidate(fromX, fromY, tryX.x, tryX.y, colliders, radius, false, 0)) {
    nx = tryX.x;
    ny = tryX.y;
  }

  const tryY = bounds ? clampToMoveBounds(nx, y, bounds) : { x: nx, y };
  if (acceptCandidate(fromX, fromY, tryY.x, tryY.y, colliders, radius, false, 0)) {
    nx = tryY.x;
    ny = tryY.y;
  }

  return { x: nx, y: ny };
}

/**
 * Jeden krok w stronę celu z prędkością i kolizjami.
 */
export function stepToward(
  x: number,
  y: number,
  tx: number,
  ty: number,
  dtSec: number,
  speed: number,
  colliders: readonly ColliderRect[],
  radius: number,
  bounds?: WorldMoveBounds,
): { x: number; y: number; arrived: boolean } {
  const dx = tx - x;
  const dy = ty - y;
  const dist = Math.hypot(dx, dy);
  if (dist < 1) {
    const at = resolveMovement(x, y, tx, ty, colliders, radius, bounds);
    return { ...at, arrived: Math.hypot(at.x - tx, at.y - ty) < 1.5 };
  }
  const step = Math.min(dist, speed * Math.max(0, dtSec));
  const toX = x + (dx / dist) * step;
  const toY = y + (dy / dist) * step;
  const next = resolveMovement(x, y, toX, toY, colliders, radius, bounds);
  const moved = Math.hypot(next.x - x, next.y - y);
  // Zablokowany przez ścianę (cel za blokadą) — nie „przybył", ale nie poszedł dalej
  const arrived = dist - moved < 1.5 && !collidesAt(tx, ty, colliders, radius);
  return { ...next, arrived: arrived || (moved < 0.01 && dist < speed * dtSec + 2) };
}
